// @flow
import { of } from 'rxjs';
import { mergeMap, map, catchError, debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import update from 'immutability-helper';
import {
  endpoints,
  getAvailableRequestsApi,
  getFlightApi,
  getFlightSalesApi,
  getOnlineStores,
  getTravelerRewardApi,
} from './api';
import Paginator from '../../common/Paginator';
import { setNewAlert } from '../alert/duck';
import { ofType, StateObservable } from 'redux-observable';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Flight, Order, Transaction, OnlineStore } from '../../entities';
import type { RootState } from '../index';

export type TravelerReward = {
  nextReward: number;
  progress: number;
  reservationCount: number;
  reward: string;
  total: number;
};

type FlightAction = {
  +type: string;
  +fetchRequestsError?: HTTPError;
  +availableOrders?: Order[];
  +fetchSalesError?: HTTPError;
  +flightSales?: Transaction[];
  +queryFilter?: { [key: string]: string };
  +resetPagination?: boolean;
  +id?: string;
  +flight?: Flight;
  +fetchFlightError?: HTTPError;
  // +onlineStores?: OnlineStore[];
  // +fetchOnlineStoresError?: HTTPError;
  +travelerReward?: TravelerReward;
  +error?: HTTPError;
};

export type FlightState = {
  +isFetchingFlight?: boolean;
  +flight?: Flight;
  +fetchFlightError?: HTTPError;

  +isFetchingAvailable?: boolean;
  +availableOrders?: Order[];
  +availableOrdersPage?: number;
  +availableOrdersPageCanAsk?: boolean;
  +fetchRequestsError?: HTTPError;

  +isFetchingSales?: boolean;
  +flightSales?: Transaction[];
  +flightSalesPage?: number;
  +flightSalesPageCanAsk?: boolean;
  +fetchSalesError?: HTTPError;

  +isFetchingReward?: boolean;
  +travelerReward?: TravelerReward;
  +fetchCountError?: HTTPError;
  // +isFetchingOnlineStores?: boolean;
  // +onlineStores?: OnlineStore[];
  // +fetchOnlineStoresError?: HTTPError;
};

// Actions
const FLIGHT_RESET_STATE = 'FLIGHT_RESET_STATE';
const FLIGHT_RESET_ORDER_REQUESTS_STATE = 'FLIGHT_RESET_ORDER_REQUESTS_STATE';
const FLIGHT_RESET_FLIGHT_SALES_STATE = 'FLIGHT_RESET_FLIGHT_SALES_STATE';

const FLIGHT_FETCH_FLIGHT_REQUEST = 'FLIGHT_FETCH_FLIGHT_REQUEST';
const FLIGHT_FETCH_FLIGHT_SUCCESS = 'FLIGHT_FETCH_FLIGHT_SUCCESS';
const FLIGHT_FETCH_FLIGHT_ERROR = 'FLIGHT_FETCH_FLIGHT_ERROR';

const FLIGHT_FETCH_REQUESTS_REQUEST = 'FLIGHT_FETCH_REQUESTS_REQUEST';
const FLIGHT_FETCH_REQUESTS_SUCCESS = 'FLIGHT_FETCH_REQUESTS_SUCCESS';
const FLIGHT_FETCH_REQUESTS_ERROR = 'FLIGHT_FETCH_REQUESTS_ERROR';

const FLIGHT_FETCH_SALES_REQUEST = 'FLIGHT_FETCH_SALES_REQUEST';
const FLIGHT_FETCH_SALES_SUCCESS = 'FLIGHT_FETCH_SALES_SUCCESS';
const FLIGHT_FETCH_SALES_ERROR = 'FLIGHT_FETCH_SALES_ERROR';

const FLIGHT_FETCH_TRAVELER_REWARD_REQUEST = 'FLIGHT_FETCH_TRAVELER_REWARD_REQUEST';
const FLIGHT_FETCH_TRAVELER_REWARD_SUCCESS = 'FLIGHT_FETCH_TRAVELER_REWARD_SUCCESS';
const FLIGHT_FETCH_TRAVELER_REWARD_ERROR = 'FLIGHT_FETCH_TRAVELER_REWARD_ERROR';

// const FLIGHT_FETCH_ONLINE_STORES_REQUEST = 'FLIGHT_FETCH_ONLINE_STORES_REQUEST';
// const FLIGHT_FETCH_ONLINE_STORES_SUCCESS = 'FLIGHT_FETCH_ONLINE_STORES_SUCCESS';
// const FLIGHT_FETCH_ONLINE_STORES_ERROR = 'FLIGHT_FETCH_ONLINE_STORES_ERROR';

// Action Creators
export function fetchFlight(id: string) {
  return {
    type: FLIGHT_FETCH_FLIGHT_REQUEST,
    id,
  };
}

export function fetchFlightSuccess(flight: Flight) {
  return {
    type: FLIGHT_FETCH_FLIGHT_SUCCESS,
    flight,
  };
}

export function fetchFlightErrorFn(fetchFlightError: HTTPError): FlightAction {
  return {
    type: FLIGHT_FETCH_FLIGHT_ERROR,
    fetchFlightError,
  }
}

export function fetchAvailableRequests(id: string, queryFilter: { [key: string]: string } = {},
                                       resetPagination?: boolean): FlightAction {
  return {
    type: FLIGHT_FETCH_REQUESTS_REQUEST,
    id,
    queryFilter,
    resetPagination,
  };
}

export function fetchAvailableRequestsSuccess(availableOrders: Order[]): FlightAction {
  return {
    type: FLIGHT_FETCH_REQUESTS_SUCCESS,
    availableOrders,
  };
}

export function fetchAvailableRequestsError(fetchRequestsError: HTTPError): FlightAction {
  return {
    type: FLIGHT_FETCH_REQUESTS_ERROR,
    fetchRequestsError,
  }
}

export function fetchFlightSales(id: string, queryFilter: { [key: string]: string } = {},
                                 resetPagination?: boolean): FlightAction {
  return {
    type: FLIGHT_FETCH_SALES_REQUEST,
    id,
    queryFilter,
    resetPagination,
  };
}

export function fetchFlightSalesSuccess(flightSales: any /*Transaction[]*/): FlightAction {
  return {
    type: FLIGHT_FETCH_SALES_SUCCESS,
    flightSales,
  };
}

export function fetchFlightSalesError(fetchSalesError: HTTPError): FlightAction {
  return {
    type: FLIGHT_FETCH_SALES_ERROR,
    fetchSalesError,
  };
}

export function fetchFlightTravelerReward(id: string): FlightAction {
  return {
    type: FLIGHT_FETCH_TRAVELER_REWARD_REQUEST,
    id,
  };
}

export function fetchFlightTravelerRewardSuccess(travelerReward: TravelerReward): FlightAction {
  return {
    type: FLIGHT_FETCH_TRAVELER_REWARD_SUCCESS,
    travelerReward,
  };
}

export function fetchFlightTravelerRewardError(error: HTTPError): FlightAction {
  return {
    type: FLIGHT_FETCH_TRAVELER_REWARD_ERROR,
    error,
  };
}

/*
export function fetchOnlineStores(): FlightAction {
  return {
    type: FLIGHT_FETCH_ONLINE_STORES_REQUEST,
  };
}

export function fetchOnlineStoresSuccess(onlineStores: OnlineStore[]): FlightAction {
  return {
    type: FLIGHT_FETCH_ONLINE_STORES_SUCCESS,
    onlineStores,
  };
}

export function fetchOnlineStoresErrorFn(fetchOnlineStoresError: HTTPError): FlightAction {
  return {
    type: FLIGHT_FETCH_ONLINE_STORES_ERROR,
    fetchOnlineStoresError,
  }
}
*/
export function resetFlightState(): FlightAction {
  return {
    type: FLIGHT_RESET_STATE,
  };
}

export function resetOrderRequestsState(): FlightAction {
  return {
    type: FLIGHT_RESET_ORDER_REQUESTS_STATE,
  };
}

export function resetFlightSalesState(): FlightAction {
  return {
    type: FLIGHT_RESET_FLIGHT_SALES_STATE,
  };
}

export const actions = {
  FLIGHT_FETCH_FLIGHT_REQUEST,
  FLIGHT_FETCH_FLIGHT_SUCCESS,
  FLIGHT_FETCH_FLIGHT_ERROR,
  FLIGHT_FETCH_REQUESTS_REQUEST,
  FLIGHT_FETCH_REQUESTS_SUCCESS,
  FLIGHT_FETCH_REQUESTS_ERROR,
  FLIGHT_FETCH_SALES_REQUEST,
  FLIGHT_FETCH_SALES_SUCCESS,
  FLIGHT_FETCH_SALES_ERROR,
  FLIGHT_RESET_STATE,
  FLIGHT_RESET_ORDER_REQUESTS_STATE,
  FLIGHT_RESET_FLIGHT_SALES_STATE,
  FLIGHT_FETCH_TRAVELER_REWARD_REQUEST,
  FLIGHT_FETCH_TRAVELER_REWARD_SUCCESS,
  FLIGHT_FETCH_TRAVELER_REWARD_ERROR,
  fetchFlight,
  fetchFlightSuccess,
  fetchFlightErrorFn,
  fetchAvailableRequests,
  fetchAvailableRequestsSuccess,
  fetchAvailableRequestsError,
  fetchFlightSales,
  fetchFlightSalesSuccess,
  fetchFlightSalesError,
  // fetchOnlineStores,
  // fetchOnlineStoresSuccess,
  // fetchOnlineStoresErrorFn,
  fetchFlightTravelerReward,
  fetchFlightTravelerRewardSuccess,
  fetchFlightTravelerRewardError,
  resetFlightState,
  resetOrderRequestsState,
  resetFlightSalesState,
};

export const initialState: FlightState = {
  availableOrdersPage: 0,
  // flightSalesPage: 0,
};

const paginator = new Paginator(['availableOrders']);

// Reducer
export default function reducer(state: FlightState = initialState,
                                action: FlightAction): FlightState {
  switch (action.type) {
    case FLIGHT_FETCH_FLIGHT_REQUEST:
      return update(state, {
        isFetchingFlight: { $set: true }
      });
    case FLIGHT_FETCH_FLIGHT_SUCCESS:
      return update(state, {
        $unset: ['isFetchingFlight'],
        flight: { $set: action.flight }
      });
    case FLIGHT_FETCH_FLIGHT_ERROR:
      return update(state, {
        fetchFlightError: { $set: action.fetchFlightError }
      });
    case FLIGHT_FETCH_REQUESTS_REQUEST:
      if (action.resetPagination) {
        paginator.reset('availableOrders');
        return update(state, {
          isFetchingAvailable: { $set: true },
          availableOrders: { $set: paginator.getItems('availableOrders') },
          availableOrdersPageCanAsk: { $set: paginator.getCanAsk('availableOrders') },
          availableOrdersPage: { $set: paginator.getPage('availableOrders') },
        });
      }
      return update(state, {
        isFetchingAvailable: { $set: true }
      });
    case FLIGHT_FETCH_REQUESTS_SUCCESS:
      paginator.increment('availableOrders', action.availableOrders, state.availableOrders);
      return update(state, {
        $unset: ['isFetchingAvailable'],
        availableOrders: { $set: paginator.getItems('availableOrders') },
        availableOrdersPageCanAsk: { $set: paginator.getCanAsk('availableOrders') },
        availableOrdersPage: { $set: paginator.getPage('availableOrders') },
      });
    case FLIGHT_FETCH_REQUESTS_ERROR:
      return update(state, {
        $unset: ['isFetchingAvailable'],
        fetchRequestsError: { $set: action.fetchRequestsError },
        availableOrdersPage: { $set: state.availableOrdersPage },
      });

    case FLIGHT_FETCH_SALES_REQUEST:
      return update(state, {
        isFetchingSales: { $set: true },
      });
    case FLIGHT_FETCH_SALES_SUCCESS:
      return update(state, {
        $unset: ['isFetchingSales'],
        flightSales: { $set: action.flightSales },
      });
    case FLIGHT_FETCH_SALES_ERROR:
      return update(state, {
        $unset: ['isFetchingSales'],
        fetchSalesError: { $set: action.fetchSalesError },
      });
    case FLIGHT_FETCH_TRAVELER_REWARD_REQUEST:
      return update(state, {
        isFetchingReward: { $set: true },
      });
    case FLIGHT_FETCH_TRAVELER_REWARD_SUCCESS:
      return update(state, {
        $unset: ['isFetchingReward'],
        travelerReward: { $set: action.travelerReward },
      });
    case FLIGHT_FETCH_TRAVELER_REWARD_ERROR:
      return update(state, {
        $unset: ['isFetchingReward'],
        fetchCountError: { $set: action.error },
      });
    /*case FLIGHT_FETCH_ONLINE_STORES_REQUEST:
      return update(state, {
        isFetchingOnlineStores: { $set: true },
      });
    case FLIGHT_FETCH_ONLINE_STORES_SUCCESS:
      return update(state, {
        $unset: ['isFetchingOnlineStores'],
        onlineStores: { $set: action.onlineStores },
      });
    case FLIGHT_FETCH_ONLINE_STORES_ERROR:
      return update(state, {
        $unset: ['isFetchingOnlineStores'],
        fetchOnlineStoresError: { $set: action.fetchOnlineStoresError },
      });*/
    case FLIGHT_RESET_STATE:
      paginator.resetAll();
      return initialState;
    case FLIGHT_RESET_ORDER_REQUESTS_STATE:
      paginator.reset('availableOrders');
      return update(initialState, {
        flight: { $set: state.flight },
        travelerReward: { $set: state.travelerReward },
      });
    case FLIGHT_RESET_FLIGHT_SALES_STATE:
      return update(initialState, {
        flight: { $set: state.flight },
        travelerReward: { $set: state.travelerReward },
      });
    default:
      return state;
  }
}

// Epics
export function fetchFlightFlightEpic(action$: Observable<FlightAction>): Observable<FlightAction> {
  return action$.pipe(
    ofType(FLIGHT_FETCH_FLIGHT_REQUEST),
    mergeMap(({ id }) => getFlightApi(id, {
        route: endpoints.GET_FLIGHT.replace(':id', id),
      }).pipe(
      map((response: Flight) => fetchFlightSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchFlightErrorFn(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchFlightErrorFn(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchFlightErrorFn(e))
      }))
    ));
}

export function fetchAvailableRequestsEpic(action$: Observable<FlightAction>,
                                           state$: StateObservable<RootState>): Observable<FlightAction> {
  return action$.pipe(
    ofType(FLIGHT_FETCH_REQUESTS_REQUEST),
    debounceTime(600),
    switchMap(({ id, queryFilter }) => getAvailableRequestsApi({
        ...queryFilter,
        page: state$.value.flight.availableOrdersPage || paginator.getPage('availableOrders'),
        limit: paginator.getLimit(),
      }, {
        route: endpoints.GET_AVAILABLE_REQUESTS.replace(':id', id),
      }).pipe(
      takeUntil(action$.pipe(ofType(FLIGHT_RESET_STATE))),
      takeUntil(action$.pipe(ofType(FLIGHT_FETCH_REQUESTS_REQUEST))),
      map((response: Order[]) => fetchAvailableRequestsSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchAvailableRequestsError(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          if (e.data && e.data.name === 'FlightDateTooNearError') {
            return of(fetchAvailableRequestsError(e),
              setNewAlert('flight', 'error', undefined, 'flight.requests.error.dateTooNear'),
            );
          }
          if (e.data && e.data.name === 'InvalidStateError') {
            return of(fetchAvailableRequestsError(e),
              setNewAlert('flight', 'error', undefined, 'flight.requests.error.stateInvalid'),
            );
          }
          return of(fetchAvailableRequestsError(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchAvailableRequestsError(e))
      }))
    ));
}

export function fetchFlightSalesEpic(action$: Observable<FlightAction>): Observable<FlightAction> {
  return action$.pipe(
    ofType(FLIGHT_FETCH_SALES_REQUEST),
    switchMap(({ id }) => getFlightSalesApi({
        limit: 100,
      }, {
        route: endpoints.GET_FLIGHT_SALES.replace(':id', id),
      }).pipe(
      map((response: Transaction[]) => fetchFlightSalesSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchFlightSalesError(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchFlightSalesError(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchFlightSalesError(e))
      }))
    ));
}

export function fetchTravelerRewardEpic(action$: Observable<FlightAction>): Observable<FlightAction> {
  return action$.pipe(
    ofType(FLIGHT_FETCH_TRAVELER_REWARD_REQUEST),
    mergeMap(({ id }) => getTravelerRewardApi({}, {
      route: endpoints.GET_TRAVELER_REWARD.replace(':flightId', id),
    }).pipe(
      map((travelerReward: TravelerReward) => {
        return fetchFlightTravelerRewardSuccess(travelerReward)
      }),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchFlightTravelerRewardError(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchFlightTravelerRewardError(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchFlightTravelerRewardError(e))
      }),
    )),
  );
}

/*
export function fetchOnlineStoresEpic(action$: Observable<FlightAction>): Observable<FlightAction> {
  return action$.pipe(
    ofType(FLIGHT_FETCH_ONLINE_STORES_REQUEST),
    mergeMap(() => getOnlineStores().pipe(
      map((response: OnlineStore[]) => fetchOnlineStoresSuccess(response)),
      catchError((e: HTTPError) => {
        if (Math.round(e.code / 100) === 5) {
          return of(fetchOnlineStoresErrorFn(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.500'),
          );
        } else if (e.code === 400) {
          return of(fetchOnlineStoresErrorFn(e),
            setNewAlert('flight', 'error', undefined, 'httpErrors.fetch.400'),
          );
        }
        return of(fetchOnlineStoresErrorFn(e))
      }))
    ));
}
*/



// WEBPACK FOOTER //
// ./src/modules/flight/duck.js